import React, { useState } from "react";
import "./style.css";

// MUI imports
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  Tooltip,
} from "@mui/material";

import {
  Info as InfoIcon,
  LocalShipping as ShippingIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  CompareArrows as CompareArrowsIcon,
  Inventory2Outlined as InventoryIcon,
} from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WarningIcon from "@mui/icons-material/Warning";

const GILROY_MEDIUM = "gilroyMedium";
const GILROY_BOLD = "GilroyBold";

// ---------- helpers ----------
const toDash = (v) =>
  v === undefined || v === null || String(v).trim() === "" ? "-" : v;

const firstDisplayValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "-";
};

const formatMatchingType = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized || normalized === "-") return "-";

  return normalized
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const pick = (obj, keys) => {
  for (const k of keys) {
    if (
      obj &&
      obj[k] !== undefined &&
      obj[k] !== null &&
      String(obj[k]).trim() !== ""
    ) {
      return obj[k];
    }
  }
  return undefined;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const parseMoney = (s) => {
  if (s === null || s === undefined) return null;
  if (typeof s === "number") return Number.isFinite(s) ? s : null;

  const str = String(s).trim();
  if (!str) return null;

  // Keep only digits, minus and dot so we can parse values like:
  // "â‚¹4,89,000", "489,000.00", "â‚¹12,31,850", etc.
  const normalized = str.replace(/[^\d.-]+/g, "");
  if (
    !normalized ||
    normalized === "-" ||
    normalized === "." ||
    normalized === "-."
  ) {
    return null;
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const normalizeBackendStatus = (status) =>
  String(status || "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

const isSuccessfulBackendStatus = (status) => {
  if (status === true) return true;

  const normalized = normalizeBackendStatus(status);
  if (!normalized) return false;

  if (
    normalized.includes("NOT MATCHED") ||
    normalized.includes("UNMATCHED") ||
    normalized.includes("MISMATCH") ||
    normalized.includes("EXCEED") ||
    normalized.includes("REJECT") ||
    normalized.includes("FAILED") ||
    normalized.includes("EXCEPTION")
  ) {
    return false;
  }

  return (
    normalized === "MATCHED" ||
    normalized === "FULL MATCHED" ||
    normalized === "FULLMATCHED" ||
    normalized === "FULLY MATCHED" ||
    normalized === "FULLYMATCHED" ||
    normalized === "2 WAY MATCHED" ||
    normalized === "2WAY MATCHED" ||
    normalized === "TWO WAY MATCHED" ||
    normalized === "TWOWAY MATCHED" ||
    normalized.endsWith(" MATCHED") ||
    normalized.includes("ITEM MATCHED") ||
    normalized.includes("PRICE MATCHED") ||
    normalized.includes("AMOUNT MATCHED") ||
    normalized.includes("QUANTITY MATCHED") ||
    ["TRUE", "COMPLETED", "APPROVED", "SUCCESS", "SUCCESSFUL"].includes(
      normalized
    )
  );
};

const toTitleStatus = (status) => {
  if (status === true) return "Matched";
  if (status === false) return "Not Matched";

  return String(status ?? "").trim() || "Not available";
};

const backendStatusToChip = (status) => {
  const normalized = normalizeBackendStatus(status);
  if (!normalized) return { status: "Not available", type: "notCompared" };

  if (!normalized.includes("PARTIAL") && isSuccessfulBackendStatus(status)) {
    return { status: toTitleStatus(status), type: "matched" };
  }

  if (
    normalized.includes("PO MISSING") ||
    normalized.includes("INVOICE MISSING") ||
    normalized.includes("GRN MISSING") ||
    normalized.includes("MRN MISSING") ||
    normalized.includes("MISSING") ||
    normalized.includes("UNABLE TO MATCH") ||
    normalized.includes("UNVERIFIED")
  ) {
    return { status: toTitleStatus(status), type: "missingDependency" };
  }

  if (
    normalized.includes("NOT COMPARED") ||
    normalized.includes("NOT APPLICABLE")
  ) {
    return { status: toTitleStatus(status), type: "notCompared" };
  }

  if (normalized.includes("INCOMPLETE") || normalized.includes("IN COMPLETE")) {
    return { status: toTitleStatus(status), type: "incomplete" };
  }

  if (
    normalized.includes("MISMATCH") ||
    normalized.includes("EXCEED") ||
    normalized.includes("REJECT") ||
    normalized.includes("FAILED")
  ) {
    return { status: toTitleStatus(status), type: "rejected" };
  }

  if (normalized.includes("PARTIAL")) {
    return { status: toTitleStatus(status), type: "partial" };
  }

  return { status: toTitleStatus(status), type: "partial" };
};

// Comparison fields are supplied by the matching response; missing values stay empty.
const buildResponseRows = (details) => asArray(details?.items).map((item) => ({
  item: item.item_name ?? "-",
  poItemName: item.po_item_name,
  invoiceItemName: item.invoice_item_name,
  grnItemName: item.grn_item_name,
  itemCode: toDash(item.item_code),
  poQty: toDash(item.po_qty),
  invoiceQty: toDash(item.invoice_qty),
  grnQty: toDash(item.grn_qty),
  unitPricePO: item.po_unit_price,
  unitPriceInvoice: item.invoice_unit_price,
  totalPricePO: item.po_line_amount,
  totalPriceInvoice: item.invoice_line_amount,
  taxRate: item.invoice_tax_rate ?? item.po_tax_rate,
  quantityVariance: item.variances?.quantity_variance,
  priceVariance: item.variances?.price_variance,
  itemStatus: backendStatusToChip(item.statuses?.item_status),
  quantityStatus: backendStatusToChip(item.statuses?.quantity_status),
  priceStatus: backendStatusToChip(item.statuses?.price_status),
  lineMatchStatus: backendStatusToChip(item.statuses?.line_match_status),
  statusReasons: item.status_reasons || {},
}));

// Status Chip Component for modal
const ModalStatusChip = ({ status, type, fontWeight = 600 }) => {
  const config = {
    missingDependency: {
      color: "#7C2D12",
      bgcolor: "#FFF7ED",
      icon: <WarningIcon style={{ fontSize: 14 }} />,
      border: "#FDBA74",
    },
    incomplete: {
      color: "#B45309",
      bgcolor: "#FEF3C7",
      icon: <WarningIcon style={{ fontSize: 14 }} />,
      border: "#F59E0B",
    },
    notCompared: {
      color: "#475569",
      bgcolor: "#F1F5F9",
      icon: <InfoIcon style={{ fontSize: 14 }} />,
      border: "#CBD5E1",
    },
    matched: {
      color: "#2E7D32",
      bgcolor: "#E8F5E9",
      icon: <CheckCircleIcon style={{ fontSize: 14 }} />,
    },
    partial: {
      color: "#ED6C02",
      bgcolor: "#FFF3E0",
      icon: <WarningIcon style={{ fontSize: 14 }} />,
    },
    rejected: {
      color: "#D32F2F",
      bgcolor: "#FFEBEE",
      icon: <CancelIcon style={{ fontSize: 14 }} />,
    },
    default: { color: "#757575", bgcolor: "#FAFAFA", icon: "?" },
  };

  const { color, bgcolor, icon, border } = config[type] || config.default;
  const chipBorder = border || `${color}30`;

  return (
    <Chip
      icon={icon}
      label={status}
      size="small"
      sx={{
        fontFamily: GILROY_MEDIUM,
        fontSize: "14px",
        fontWeight,
        height: 30,
        minWidth: 110,
        width: "max-content",
        maxWidth: "none",
        flexShrink: 0,
        backgroundColor: bgcolor,
        color: color,
        border: `1px solid ${chipBorder}`,
        "& .MuiChip-label": {
          px: 1,
          whiteSpace: "nowrap",
          overflow: "visible",
          textOverflow: "clip",
          wordBreak: "normal",
        },
        "& .MuiChip-icon": {
          color: color,
          fontSize: "14px",
        },
      }}
    />
  );
};

// ---------- Component ----------
const ThreeWayMatchTable = ({
  poData,
  invoiceData,
  grnData,
  paymentAdviceData,
  matchingDetails,
  open,
  onClose,
  showSummary,
  selectedCard,
  fullPageView = false,
}) => {
  const modalData = buildResponseRows(matchingDetails);
  const [showModal, setShowModal] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});


  const closeDialog = () => {
    if (typeof onClose === "function") onClose();
    setShowModal(false);
  };
  const formatNumber = (v) => {
    const n = parseMoney(v);
    if (n === 0) return "0.00";
    if (n == null) return "-";
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatCurrency = (value) => {
    const formatted = formatNumber(value);
    return formatted === "-" ? "-" : `₹ ${formatted}`;
  };

  const getDocumentTotalValue = (document = {}, keys = []) => {
    const value = pick(document, keys);
    return value === undefined ? null : value;
  };

  const getPoTotal = (keys = []) => getDocumentTotalValue(poData?.[0], keys);
  const getInvoiceTotal = (keys = []) =>
    getDocumentTotalValue(invoiceData?.[0], keys);
  const getGrnTotal = (keys = []) => getDocumentTotalValue(grnData?.[0], keys);
  const payment_advice_data = paymentAdviceData;
  const paymentAdvice = payment_advice_data?.[0];
  const po = poData?.[0];
  const grn = grnData?.[0];
  const invoice = invoiceData?.[0];
  const paymentReconciliation = {
    ...(matchingDetails?.payment_advice_summary || {}),
    ...(matchingDetails?.payment_reconciliation || {}),
    ...(matchingDetails?.payment_advice_reconciliation || {}),
    ...(matchingDetails?.result_json?.payment_advice_matching || {}),
    ...(matchingDetails?.payment_advice_matching || {}),
    ...(matchingDetails || {}),
  };
  const reconciliationInvoice = paymentReconciliation?.invoice || invoice || {};
  const mismatchReasonSource =
    paymentReconciliation?.mismatch_reasons ??
    matchingDetails?.payment_advice_matching?.mismatch_reasons;
  const mismatchReasons = Array.isArray(mismatchReasonSource)
    ? mismatchReasonSource.filter(Boolean)
    : [];
  const matchingType = String(matchingDetails?.matching_mode || selectedCard?.matching_mode || "")
    .replace(/[\s_-]/g, "")
    .toUpperCase();
  const isTwoWay = ["TWOWAY", "2WAY"].includes(matchingType);
  const hidePaymentAdvice = isTwoWay || ["THREEWAY", "3WAY"].includes(matchingType);
  const hasPaymentAdvice =
    !hidePaymentAdvice &&
    (["FOURWAY", "4WAY"].includes(matchingType) ||
      (Array.isArray(payment_advice_data) && payment_advice_data.length > 0) ||
      Boolean(
        paymentReconciliation?.payment_status ||
          reconciliationInvoice?.advance_paid !== undefined ||
          reconciliationInvoice?.expected_balance !== undefined,
      ));

  const totalKeys = {
    gross: [
      "total_tax_included",
      "tax_included_total",
      "total_with_tax",
      "invoice_amount",
      "total_amount",
      "grand_total",
      "amount",
    ],
    taxExcluded: [
      "total_tax_excluded",
      "tax_excluded_total",
      "total_without_tax",
      "taxable_amount",
      "subtotal",
    ],
    taxIncluded: [
      "total_tax_included",
      "tax_included_total",
      "total_with_tax",
      "grand_total",
      "invoice_amount",
      "total_amount",
    ],
  };

  const formatVariance = (value) => {
    const number = parseMoney(value);
    if (number === null) return "-";
    return `${number > 0 ? "+" : ""}${number.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;
  };

  const getVarianceTooltip = (row, type) =>
    row.statusReasons[`${type}_status`] || "No reason provided";

  const rawAddress = (() => {
    const addr = poData?.[0]?.shipping_address;

    if (!addr) return "No shipping address provided";

    if (typeof addr === "string") return addr;

    if (typeof addr === "object" && addr.address) return addr.address;

    return "No shipping address provided";
  })();

  const addressLines = rawAddress.split("\n");
  const stickySerialWidth = 72;
  const stickyDescriptionWidth = 220;
  const stickyOverallMatchWidth = 260;
  const stickyColumnHeaderBg = "#eaf2ff";
  const stickyColumnBodyBg = "#f8fbff";

  const fullPageSummaryItems = [
    {
      label: "TWM ID",
      value: firstDisplayValue(
        selectedCard?.match_code,
        selectedCard?.twm_code,
        matchingDetails?.match_code,
        matchingDetails?.twm_code
      ),
      tone: "primary",
    },
    {
      label: "Matching Type",
      value: formatMatchingType(
        firstDisplayValue(
          selectedCard?.matching_mode,
          matchingDetails?.matching_mode
        )
      ),
      tone: "info",
    },
    {
      label: "Supplier",
      value: firstDisplayValue(
        po?.vendor,
        po?.vendor_name,
        po?.supplier_name,
        invoice?.vendor_name,
        invoice?.supplier_name,
        grn?.vendor_name,
        grn?.vendor
      ),
    },
    {
      label: "PO Number",
      value: firstDisplayValue(
        po?.purchase_order_no,
        po?.purchase_order_number,
        po?.po_number,
        po?.po_no,
        invoice?.purchase_order_number,
        grn?.purchase_order_no
      ),
    },
    {
      label: "Invoice Number",
      value: firstDisplayValue(
        invoice?.invoice_number,
        invoice?.invoice_no,
        invoice?.bill_number,
        invoice?.bill_no
      ),
    },
    {
      label: "GRN Number",
      value: firstDisplayValue(
        grn?.grn_number,
        grn?.grn_no,
        grn?.goods_receipt_no,
        grn?.receipt_number,
        grn?.order_number
      ),
    },
    {
      label: "Status",
      value: firstDisplayValue(
        matchingDetails?.overall_status,
        matchingDetails?.status,
        selectedCard?.status,
        selectedCard?.approval_status
      ),
      tone: "status",
    },
    {
      label: "Line Items",
      value: toDash(matchingDetails?.counts?.po_items),
    },
  ].filter(item => !isTwoWay || item.label !== "GRN Number");

  const summaryToneSx = {
    primary: { borderColor: "#cfd5df", backgroundColor: "#ffffff" },
    info: { borderColor: "#cfd5df", backgroundColor: "#ffffff" },
    status: { borderColor: "#98e8d0", backgroundColor: "#eef7ff" },
  };

  const renderFullPageSummary = () => {
    if (!fullPageView) return null;

    return (
      <Paper
        className="matching-view-summary"
        elevation={0}
        sx={{
          border: 0,
          borderRadius: 0,
          backgroundColor: "transparent",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: "4px",
          }}
        >
          {fullPageSummaryItems.map((item) => (
            <Box
              key={item.label}
              data-field={item.label}
              sx={{
                minHeight: 66,
                border: "1px solid #cfd5df",
                borderRadius: 0,
                backgroundColor: "#ffffff",
                px: 1.6,
                py: 1.3,
                ...summaryToneSx[item.tone],
              }}
            >
              <Typography
                sx={{
                  color: "#64748b",
                  fontFamily: GILROY_MEDIUM,
                  fontSize: fullPageView ? "14px" : "10px",
                  fontWeight: 700,
                  letterSpacing: 0,
                  lineHeight: 1.2,
                  mb: 0.6,
                  textTransform: "uppercase",
                }}
              >
                {item.label}
              </Typography>
              <Typography
                title={String(item.value || "-")}
                sx={{
                  color: "#0f172a",
                  fontFamily: GILROY_BOLD,
                  fontSize: fullPageView ? "15px" : "12px",
                  lineHeight: 1.35,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {toDash(item.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    );
  };

  const toggleDescription = (rowIndex) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex],
    }));
  };

  const handleViewItems = () => setShowModal(true);

  const summaryHeaderCellSx = {
    fontWeight: 600,
    fontSize: "14px",
    fontFamily: GILROY_BOLD,
    color: "#0f172a",
    backgroundColor: "#E8F0F4",
    borderBottom: "1px solid #e2e8f0",
    py: 1.1,
    px: 1.5,
  };

  const summaryKeyCellSx = {
    fontWeight: 600,
    fontSize: "14px",
    fontFamily: GILROY_BOLD,
    color: "#0f172a",
    borderBottom: "1px solid #e2e8f0",
    py: 1.2,
    px: 1.5,
    width: 210,
    minWidth: 210,
  };

  const summaryValueCellSx = {
    fontSize: "14px",
    fontFamily: `${GILROY_MEDIUM} !important`,
    fontWeight: "400 !important",
    color: "#0f172a",
    borderBottom: "1px solid #e2e8f0",
    py: 1.2,
    px: 1.5,
    verticalAlign: "middle",
  };

  const renderRow = (
    label,
    poValue,
    invoiceValue,
    grnValue,
    paymentAdviceValue,
  ) => (
    <TableRow>
      <TableCell sx={summaryKeyCellSx}>{label}</TableCell>
      <TableCell sx={{ ...summaryValueCellSx, width: 260, minWidth: 230 }}>
        {toDash(poValue)}
      </TableCell>
      {hasPaymentAdvice && (
        <TableCell sx={{ ...summaryValueCellSx, width: 240, minWidth: 210 }}>
          {toDash(paymentAdviceValue)}
        </TableCell>
      )}
      <TableCell sx={{ ...summaryValueCellSx, width: 210, minWidth: 190 }}>
        {toDash(invoiceValue)}
      </TableCell>
      {!isTwoWay && (
        <TableCell sx={{ ...summaryValueCellSx, width: 210, minWidth: 190 }}>
          {toDash(grnValue)}
        </TableCell>
      )}
    </TableRow>
  );

  const renderTotalTile = (label, value) => (
    <Box
      className="matching-view-tile"
      data-field={label}
      sx={{
        p: 1.75,
        minWidth: 0,
        borderRadius: 2,
        backgroundColor: label.includes("Incl. Tax") ? "#eff6ff" : "#f8fafc",
        border: label.includes("Incl. Tax") ? "1px solid #c7dfff" : "1px solid #e2e8f0",
        minHeight: 88,
        gap: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography
        sx={{
          fontSize: "12px",
          fontFamily: GILROY_MEDIUM,
          letterSpacing: 0,
          textTransform: "none",
          color: "#64748b",
          mb: 0.15,
          lineHeight: 1.5,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: label.includes("Incl. Tax") ? "#1d4ed8" : "#0f172a" }}>
        <CurrencyRupeeIcon sx={{ fontSize: 18, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: { xs: "17px", sm: "18px" },
            fontFamily: GILROY_BOLD,
            fontWeight: 700,
            lineHeight: 1.2,
            color: "inherit",
            whiteSpace: "nowrap",
          }}
        >
          {formatNumber(value)}
        </Typography>
      </Box>
    </Box>
  );

  const renderPaymentDetail = (label, value) => {
    if (value === undefined || value === null || String(value).trim() === "") {
      return null;
    }
    const amountLabels = ["Advice Paid", "Balance Due", "Invoice Amount"];
    const isAmountValue = amountLabels.includes(label);

    return (
      <Box
        className="matching-view-tile"
        data-field={label}
        sx={{
          px: fullPageView ? 1.4 : 1,
          py: fullPageView ? 1.1 : 0.75,
          borderRadius: fullPageView ? 0 : 1.25,
          background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
          border: fullPageView ? "1px solid #cfd5df" : "1px solid #dbeafe",
          boxShadow: fullPageView ? "none" : "0 4px 12px rgba(37, 99, 235, 0.06)",
          minHeight: fullPageView ? 58 : 54,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontFamily: GILROY_MEDIUM,
            letterSpacing: 0,
            textTransform: "uppercase",
            color: "#2563eb",
            mb: 0.15,
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
        {isAmountValue ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CurrencyRupeeIcon sx={{ fontSize: 16, color: "#0f172a" }} />
            <Typography
              title={String(value)}
              sx={{
                fontSize: fullPageView ? "16px" : "14px",
                fontFamily: GILROY_BOLD,
                fontWeight: 700,
                lineHeight: 1.25,
                color: "#0f172a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formatNumber(value)}
            </Typography>
          </Box>
        ) : (
          <Typography
            title={String(value)}
            sx={{
              fontSize: fullPageView ? "16px" : "14px",
              fontFamily: GILROY_BOLD,
              fontWeight: 700,
              lineHeight: 1.25,
              color: "#1e3a8a",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    );
  };

  // Inline styles
  const headerCellStyle = {
    fontWeight: 700,
    fontSize: "14px",
    fontFamily: GILROY_BOLD,
    backgroundColor: "#f1f5f9",
    color: "#334155",
    padding: "14px 12px",
    whiteSpace: "nowrap",
    borderRight: "1px solid #e2e8f0",
  };

  const cellStyle = {
    fontSize: "14px",
    fontFamily: GILROY_MEDIUM,
    padding: "8px",
    borderRight: "1px solid #f0f0f0",
  };

  const renderDescriptionCell = (value, rowIndex) => {
    const text = String(value || "-").trim() || "-";
    const isExpanded = Boolean(expandedDescriptions[rowIndex]);
    const isLongText = text.length > 72;

    return (
      <Box
        sx={{
          width: stickyDescriptionWidth - 24,
          maxWidth: stickyDescriptionWidth - 24,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 0.5,
        }}
      >
          <Typography
            component="div"
            sx={{
              width: "100%",
              fontSize: fullPageView ? "14px" : "13px",
              lineHeight: 1.45,
              fontFamily: GILROY_MEDIUM,
              color: "#1e293b",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: isExpanded ? "unset" : 2,
              overflow: "hidden",
              whiteSpace: isExpanded ? "normal" : "initial",
              textOverflow: "ellipsis",
              wordBreak: "break-word",
            }}
          >
            {text}
          </Typography>

        {isLongText ? (
          <Button
            size="small"
            variant="text"
            onClick={() => toggleDescription(rowIndex)}
            sx={{
              minWidth: "auto",
              padding: 0,
              fontSize: fullPageView ? "14px" : "11px",
              lineHeight: 1.2,
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            {isExpanded ? "Read less" : "Read more"}
          </Button>
        ) : null}
      </Box>
    );
  };

  return (
    <div>
      {showSummary && (<>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "17px", color: "#0f172a" }}>Document Overview</Typography>
            <Typography sx={{ mt: 0.25, fontSize: "13px", color: "#64748b" }}>Review the document details and open the item comparison.</Typography>
          </Box>
          <Button variant="contained" disableElevation startIcon={<Inventory2OutlinedIcon />} onClick={handleViewItems}
            sx={{ textTransform: "none", fontFamily: GILROY_BOLD, fontSize: "14px", borderRadius: 2, px: 2, py: 1, whiteSpace: "nowrap" }}>
            View Compare Items
          </Button>
        </Box>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 2,
            overflowX: "auto",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            boxShadow: "0 4px 20px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Table size="small" sx={{ tableLayout: "auto", width: "100%", minWidth: hasPaymentAdvice ? 1040 : isTwoWay ? 640 : 840,
            "& tbody tr:not(:first-of-type):hover": { backgroundColor: "#f8fbff" },
            "& td": { overflowWrap: "break-word" },
          }}>
            <TableBody>
              <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                <TableCell sx={summaryHeaderCellSx}>
                  Document Details
                </TableCell>
                <TableCell sx={summaryHeaderCellSx}>
                  PO
                </TableCell>
                {hasPaymentAdvice && (
                  <TableCell sx={summaryHeaderCellSx}>
                    Payment Advice
                  </TableCell>
                )}
                <TableCell sx={summaryHeaderCellSx}>
                  Invoice
                </TableCell>
                {!isTwoWay && (
                  <TableCell sx={summaryHeaderCellSx}>
                  GRN
                </TableCell>)}
              </TableRow>
              {renderRow(
                "Document No",
                po?.purchase_order_no || po?.purchase_order_number || po?.po_number || po?.po_no || po?.order_number,
                invoice?.invoice_number || invoice?.invoice_no || invoice?.bill_no,
                grn?.grn_number || grn?.grn_no || grn?.order_number,
                paymentAdvice?.payment_advice_number
              )}
              {(invoice?.purchase_order_number || invoice?.po_number || invoice?.po_no ||
                grn?.purchase_order_no || grn?.purchase_order_number || grn?.po_number || grn?.po_no ||
                paymentAdvice?.po_number) && renderRow(
                "PO Reference",
                "-",
                invoice?.purchase_order_number || invoice?.po_number || invoice?.po_no,
                grn?.purchase_order_no || grn?.purchase_order_number || grn?.po_number || grn?.po_no,
                paymentAdvice?.po_number
              )}
              {!isTwoWay && grn?.bill_no && renderRow(
                "Invoice Reference on GRN",
                "-",
                "-",
                grn.bill_no,
                "-"
              )}
              {renderRow(
                hasPaymentAdvice ? "Supplier / Payment Mode" : "Supplier Name",
                poData?.[0]?.vendor ||
                  poData?.[0]?.vendor_name ||
                  poData?.[0]?.supplier_name,
                invoiceData?.[0]?.vendor_name ||
                  invoiceData?.[0]?.supplier_name ||
                  "-",
                grnData?.[0]?.vendor_name || grnData?.[0]?.vendor,
                paymentAdvice?.payment_product
              )}

              {renderRow(
                hasPaymentAdvice ? "Bill / Transaction Date" : "Bill Date",
                poData?.[0]?.order_date || poData?.[0]?.po_date,
                invoiceData?.[0]?.invoice_date,
                grnData?.[0]?.bill_date || grnData?.[0]?.date || grnData?.[0]?.grn_date,
                paymentAdvice?.transaction_date
              )}
              {hasPaymentAdvice &&
                renderRow(
                  "Payment Ref No",
                  "-",
                  "-",
                  "-",
                  paymentAdvice?.utr_rrn_no ||
                    paymentAdvice?.transaction_reference ||
                    paymentAdvice?.payment_reference ||
                    paymentAdvice?.payment_advice_number
                )}
              {hasPaymentAdvice &&
                renderRow(
                  "Advance Paid",
                  "-",
                  firstDisplayValue(
                    reconciliationInvoice?.advance_paid,
                    invoice?.advance_paid,
                    invoice?.advance_payment,
                    invoice?.advance_amount,
                    paymentAdvice?.advance_paid,
                    paymentAdvice?.advance_payment,
                    paymentAdvice?.advance_amount,
                  ),
                  "-",
                  firstDisplayValue(
                    paymentReconciliation?.current_upload_total_amount,
                    paymentAdvice?.advance_paid,
                    paymentAdvice?.advance_payment,
                    paymentAdvice?.advance_amount,
                  )
                )}
              {hasPaymentAdvice &&
                renderRow(
                  "Expected Balance",
                  "-",
                  firstDisplayValue(
                    reconciliationInvoice?.expected_balance,
                    invoice?.balance_due,
                    invoice?.balance_amount,
                  ),
                  "-",
                  firstDisplayValue(
                    paymentReconciliation?.remaining_advance_amount,
                    paymentAdvice?.balance_due,
                    paymentAdvice?.balance_amount,
                  )
                )}
              {hasPaymentAdvice &&
                renderRow(
                  "Invoice Balance",
                  "-",
                  reconciliationInvoice?.invoice_balance,
                  "-",
                  paymentReconciliation?.total_paid_amount,
                )}

              {renderRow(
                "Subtotal",
                formatCurrency(getPoTotal(totalKeys.taxExcluded)),
                formatCurrency(getInvoiceTotal(totalKeys.taxExcluded)),
                "-",
                formatCurrency(paymentAdvice?.amount)
              )}
              {renderRow(
                "Total Price (Incl. Tax)",
                formatCurrency(getPoTotal(totalKeys.taxIncluded)),
                formatCurrency(getInvoiceTotal(totalKeys.taxIncluded)),
                "-",
                formatCurrency(paymentAdvice?.amount)
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {mismatchReasons.length > 0 && (
          <Box
            sx={{
              m: 1.5,
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
            }}
          >
            <Typography sx={{ fontFamily: GILROY_BOLD, color: "#991b1b", mb: 0.5 }}>
              Mismatch Reasons
            </Typography>
            {mismatchReasons.map((reason, index) => (
              <Typography key={`${reason}-${index}`} sx={{ fontFamily: GILROY_MEDIUM, color: "#b91c1c", fontSize: "13px", mt: 0.4 }}>
                {index + 1}. {reason}
              </Typography>
            ))}
          </Box>
        )}
      </>)}
      {/* Modal UI */}
      <Dialog
        className={fullPageView ? "matching-detail-view" : undefined}
        open={Boolean(open) || showModal}
        onClose={closeDialog}
        fullScreen={fullPageView}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            width: fullPageView ? "100%" : { xs: "calc(100% - 16px)", sm: "calc(100% - 48px)" },
            m: fullPageView ? 0 : { xs: 1, sm: 3 },
            maxHeight: fullPageView ? "100%" : "calc(100dvh - 32px)",
            borderRadius: fullPageView ? 0 : "16px",
            overflow: "hidden",
            backgroundColor: "#f5f6fb",
          },
        }}
      >
        <DialogTitle
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
            background: fullPageView ? "#f5f6fb" : "linear-gradient(120deg, #155eae 0%, #1976d2 65%, #2487df 100%)",
            borderBottom: fullPageView ? "1px solid #e2e8f0" : "none",
            color: fullPageView ? "#0f172a" : "white",
            padding: fullPageView ? "16px 20px 12px" : "12px 24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", minWidth: 0, gap: "10px" }}>
            <AssignmentIcon
              style={{
                color: fullPageView ? "#6b7280" : "inherit",
                fontSize: fullPageView ? 24 : 28,
                flex: "0 0 auto",
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                style={{
                  fontWeight: 600,
                  fontFamily: GILROY_BOLD,
                  fontSize: fullPageView ? "20px" : "18px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Account Payable Automation Comparison
              </Typography>
              {fullPageView && (
                <Typography
                  sx={{
                    color: "#64748b",
                    fontFamily: GILROY_MEDIUM,
                    fontSize: "14px",
                    lineHeight: 1.4,
                    mt: 0.35,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  TWM ID: {fullPageSummaryItems[0].value} | Matching Type: {fullPageSummaryItems[1].value}
                </Typography>
              )}
            </Box>
          </div>
          {fullPageView && (
            <Button
              variant="contained"
              disableElevation
              startIcon={<ArrowBackIcon />}
              onClick={closeDialog}
              sx={{
                flexShrink: 0,
                minWidth: 88,
                height: 40,
                px: 2,
                borderRadius: "8px",
                backgroundColor: "#2563eb",
                fontFamily: GILROY_BOLD,
                fontSize: "14px",
                lineHeight: 1,
                textTransform: "none",
                "& .MuiButton-startIcon": { display: "flex", mr: 1, ml: 0 },
                "&:hover": { backgroundColor: "#1d4ed8" },
              }}
            >
              Back
            </Button>
          )}
        </DialogTitle>

        <DialogContent
          style={{
            padding: fullPageView ? "10px 20px 18px" : 0,
            overflow: "auto",
            backgroundColor: fullPageView ? "#f5f6fb" : "#f8fafc",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              gap: fullPageView ? 2.5 : 0,
              minWidth: 0,
            }}
          >
          {renderFullPageSummary()}
          <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: 2, minWidth: 0, backgroundColor: "#f8fbff", borderBottom: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#334155", overflowWrap: "anywhere" }}>
              {matchingDetails?.summary || "Comparison details"}
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: "13px", color: "#64748b" }}>
              Quantity: {isTwoWay ? "Invoice" : "GRN"} minus PO. Unit price: Invoice minus PO. Hover or tap a status for the reason. Scroll horizontally to see all columns.
            </Typography>
          </Box>
          <TableContainer
            component={Paper}
            style={{
              maxHeight: fullPageView ? "clamp(260px, calc(100vh - 420px), 640px)" : 500,
              maxWidth: "100%",
              flexShrink: 0,
              borderRadius: 0,
              border: fullPageView ? "1px solid #cfd5df" : "none",
              overflow: "auto",
              boxShadow: "none",
            }}
          >
            <Table
              sx={{
                minWidth: isTwoWay ? 2500 : 2640,
                tableLayout: "auto",
                borderRight: "1px solid #e2e8f0",
                "& th, & td": { minWidth: 140 },
                "& th:first-of-type, & td:first-of-type": {
                  width: stickySerialWidth,
                  minWidth: stickySerialWidth,
                  maxWidth: stickySerialWidth,
                  boxSizing: "border-box",
                  borderRight: "1px solid #e2e8f0 !important",
                },
                "& th:nth-of-type(2), & td:nth-of-type(2)": {
                  width: stickyDescriptionWidth,
                  minWidth: stickyDescriptionWidth,
                  maxWidth: stickyDescriptionWidth,
                  boxSizing: "border-box",
                  "&::after": {
                    content: '""',
                    display: { xs: "none", md: "block" },
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: "1px",
                    backgroundColor: "#cbd5e1",
                    boxShadow: "5px 0 10px 1px rgba(15, 23, 42, 0.14)",
                    pointerEvents: "none",
                  },
                },
                '& [data-column="quantity-variance"]': { minWidth: 260 },
                "& th:last-of-type, & td:last-of-type": {
                  width: stickyOverallMatchWidth,
                  minWidth: stickyOverallMatchWidth,
                  maxWidth: stickyOverallMatchWidth,
                  "& .MuiChip-root": { height: "auto", minHeight: 30, minWidth: 0, maxWidth: "100%", width: "auto" },
                  "& .MuiChip-label": { whiteSpace: "normal", wordBreak: "normal", overflowWrap: "break-word", lineHeight: 1.5, py: 0.5 },
                },
              }}
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      fontFamily: GILROY_BOLD,
                      color: "#334155",
                      position: { xs: "static", md: "sticky" },
                      left: 0,
                      zIndex: 6,
                      width: stickySerialWidth,
                      minWidth: stickySerialWidth,
                      backgroundColor: stickyColumnHeaderBg,
                      boxShadow: "2px 0 0 #e2e8f0",
                    }}
                    style={headerCellStyle}
                  >
                    S.No
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      fontFamily: GILROY_BOLD,
                      color: "#334155",
                      position: { xs: "static", md: "sticky" },
                      left: stickySerialWidth,
                      zIndex: 5,
                      width: stickyDescriptionWidth,
                      minWidth: stickyDescriptionWidth,
                      backgroundColor: stickyColumnHeaderBg,
                      boxShadow: "2px 0 0 #e2e8f0",
                    }}
                    style={headerCellStyle}
                  >
                    Item Name
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>Item Code</TableCell>
                  <TableCell data-column="po-quantity" align="center" style={headerCellStyle}>
                    PO Quantity
                  </TableCell>
                  <TableCell data-column="invoice-quantity" align="center" style={headerCellStyle}>
                    Invoice Quantity
                  </TableCell>
                  {!isTwoWay && (
                    <TableCell data-column="grn-quantity" align="center" style={headerCellStyle}>
                    GRN Quantity
                  </TableCell>)}
                  <TableCell align="center" style={headerCellStyle}>
                    Unit Price (PO)
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Unit Price (Invoice)
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Total Price (PO)
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Total Price (Invoice)
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    TAX %
                  </TableCell>
                  <TableCell data-column="quantity-variance" align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CompareArrowsIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Quantity Variance (PO vs {isTwoWay ? "Invoice" : "GRN"})
                      <Tooltip
                        arrow
                        enterTouchDelay={0}
                        title={matchingDetails?.comparison_basis?.quantity_variance || (isTwoWay ? "Invoice quantity minus PO quantity" : "GRN quantity minus PO quantity")}
                        slotProps={{
                          tooltip: { sx: { fontSize: fullPageView ? "14px" : "12px" } },
                        }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <TrendingUpIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Unit Price Variance
                      <Tooltip
                        arrow
                        enterTouchDelay={0}
                        title={matchingDetails?.comparison_basis?.price_variance || "Invoice unit price minus PO unit price"}
                        slotProps={{
                          tooltip: { sx: { fontSize: fullPageView ? "14px" : "12px" } },
                        }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  {/* New Status Columns */}
                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <InventoryIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Item Status
                      <Tooltip
                        arrow
                        enterTouchDelay={0}
                        title="Item matching status (PO vs GRN vs Invoice)"
                        slotProps={{
                          tooltip: { sx: { fontSize: fullPageView ? "14px" : "12px" } },
                        }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <TrendingUpIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Quantity Status
                      <Tooltip
                        arrow
                        enterTouchDelay={0}
                        title="Quantity matching status"
                        slotProps={{
                          tooltip: { sx: { fontSize: fullPageView ? "14px" : "12px" } },
                        }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MoneyIcon style={{ fontSize: 16, marginRight: "4px" }} />
                      Price Status
                      <Tooltip
                        arrow
                        enterTouchDelay={0}
                        title="Price matching status"
                        slotProps={{
                          tooltip: { sx: { fontSize: fullPageView ? "14px" : "12px" } },
                        }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell
                    align="center"
                    style={headerCellStyle}
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      fontFamily: GILROY_BOLD,
                      color: "#334155",
                      position: { xs: "static", md: "sticky" },
                      right: 0,
                      zIndex: 6,
                      width: stickyOverallMatchWidth,
                      minWidth: stickyOverallMatchWidth,
                      backgroundColor: stickyColumnHeaderBg,
                      borderLeft: "1px solid #e2e8f0",
                      boxShadow: "-10px 0 12px -12px rgba(15, 23, 42, 0.35)",
                    }}
                  >
                    <Tooltip
                      title="Overall matching result for this item. Hover or tap a status to see the reason."
                      arrow
                      enterTouchDelay={0}
                      slotProps={{ tooltip: { sx: { fontSize: "14px" } } }}
                    >
                      <Box component="span" tabIndex={0} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                        Line Match Status
                        <InfoIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {modalData.map((row, idx) => {
                  const rowBackground =
                    idx % 2 === 0 ? "rgba(248,250,252,0.9)" : "#ffffff";
                  const stickyBodyBackground =
                    idx % 2 === 0 ? stickyColumnBodyBg : "#ffffff";
                  const overallMatchStatus = row.lineMatchStatus;

                  return (
                  <TableRow
                    key={idx}
                    sx={{
                      "& td": {
                        py: 1.8,
                        px: 1.5,
                        borderBottom: "1px solid #f1f5f9",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontFamily: GILROY_MEDIUM,
                        fontSize: "14px",
                      },
                      "&:hover td": {
                        backgroundColor: "#eff6ff",
                      },
                    }}
                    style={{
                      backgroundColor: rowBackground,
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "14px",
                        fontFamily: GILROY_BOLD,
                        color: "#475569",
                        fontWeight: 600,
                        position: { xs: "static", md: "sticky" },
                        left: 0,
                        zIndex: 4,
                        width: stickySerialWidth,
                        minWidth: stickySerialWidth,
                        backgroundColor: stickyBodyBackground,
                        boxShadow: "2px 0 0 #e2e8f0",
                      }}
                      style={cellStyle}
                    >
                      {idx + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "14px",
                        fontFamily: GILROY_MEDIUM,
                        color: "#475569",
                        fontWeight: 500,
                        position: { xs: "static", md: "sticky" },
                        left: stickySerialWidth,
                        zIndex: 3,
                        width: stickyDescriptionWidth,
                        minWidth: stickyDescriptionWidth,
                        backgroundColor: stickyBodyBackground,
                        boxShadow: "2px 0 0 #e2e8f0",
                      }}
                      style={cellStyle}
                    >
                      {renderDescriptionCell(row.item, idx)}
                    </TableCell>
                    <TableCell style={cellStyle}>
                      {row.itemCode || "-"}
                    </TableCell>
                    <TableCell data-column="po-quantity" align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        label={row.poQty}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_BOLD,
                          backgroundColor: "#e3f2fd",
                          minWidth: "60px",
                        }}
                      />
                    </TableCell>
                    <TableCell data-column="invoice-quantity" align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        label={row.invoiceQty}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_BOLD,
                          backgroundColor: "#e8f5e9",
                          minWidth: "60px",
                        }}
                      />
                    </TableCell>
                    {!isTwoWay && (
                      <TableCell data-column="grn-quantity" align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        label={row.grnQty ?? row.quantity_received}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_BOLD,
                          backgroundColor: "#f3e5f5",
                          minWidth: "60px",
                        }}
                      />
                    </TableCell>)}
                    <TableCell data-column="amount" align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.unitPricePO)}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_MEDIUM,
                          borderColor: "#bbdefb",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>
                    <TableCell data-column="amount" align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.unitPriceInvoice)}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_MEDIUM,
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>
                    <TableCell data-column="amount" align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.totalPricePO)}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_MEDIUM,
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>
                    <TableCell data-column="amount" align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.totalPriceInvoice)}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_MEDIUM,
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>

                    <TableCell data-column="amount" align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={toDash(row.taxRate)}
                    
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_MEDIUM,
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>

                    <TableCell data-column="quantity-variance" align="center" style={cellStyle}>
                      <Tooltip
                        arrow
                        enterTouchDelay={0}
                        title={getVarianceTooltip(row, "quantity")}
                        slotProps={{ tooltip: { sx: { fontSize: fullPageView ? "14px" : "13px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                          tabIndex={0}
                        >
                          <Chip
                            size="small"
                            label={formatVariance(row.quantityVariance)}
                            variant="outlined"
                            style={{
                              fontSize: "14px",
                              fontFamily: GILROY_MEDIUM,
                              fontWeight: 600,
                              borderColor:
                                row.quantityVariance === 0
                                  ? "#2E7D32"
                                  : "#D84315",
                              color:
                                row.quantityVariance === 0
                                  ? "#2E7D32"
                                  : "#D84315",
                              minWidth: "70px",
                            }}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        arrow
                        enterTouchDelay={0}
                        title={getVarianceTooltip(row, "price")}
                        slotProps={{ tooltip: { sx: { fontSize: fullPageView ? "14px" : "13px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                          tabIndex={0}
                        >
                          <Chip
                            size="small"
                            label={formatVariance(row.priceVariance, "price")}
                            variant="outlined"
                            style={{
                              fontSize: "14px",
                              fontFamily: GILROY_MEDIUM,
                              fontWeight: 600,
                              borderColor:
                                row.priceVariance === 0 ? "#2E7D32" : "#D84315",
                              color:
                                row.priceVariance === 0 ? "#2E7D32" : "#D84315",
                              minWidth: "70px",
                            }}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        arrow
                        enterTouchDelay={0}
                        title={row.statusReasons.item_status || "No reason provided"}
                        slotProps={{ tooltip: { sx: { fontSize: fullPageView ? "14px" : "13px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                          tabIndex={0}
                        >
                          <ModalStatusChip
                            status={row.itemStatus.status}
                            type={row.itemStatus.type}
                            fontWeight={400}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        arrow
                        enterTouchDelay={0}
                        title={row.statusReasons.quantity_status || "No reason provided"}
                        slotProps={{ tooltip: { sx: { fontSize: fullPageView ? "14px" : "13px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                          tabIndex={0}
                        >
                          <ModalStatusChip
                            status={row.quantityStatus.status}
                            type={row.quantityStatus.type}
                            fontWeight={400}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        arrow
                        enterTouchDelay={0}
                        title={row.statusReasons.price_status || "No reason provided"}
                        slotProps={{ tooltip: { sx: { fontSize: fullPageView ? "14px" : "13px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                          tabIndex={0}
                        >
                          <ModalStatusChip
                            status={row.priceStatus.status}
                            type={row.priceStatus.type}
                            fontWeight={400}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell
                      align="center"
                      style={cellStyle}
                      sx={{
                        fontSize: "14px",
                        color: "#475569",
                        fontWeight: 600,
                        position: { xs: "static", md: "sticky" },
                        right: 0,
                        zIndex: 5,
                        width: stickyOverallMatchWidth,
                        minWidth: stickyOverallMatchWidth,
                        backgroundColor: stickyBodyBackground,
                        borderLeft: "1px solid #e2e8f0",
                        boxShadow: "-10px 0 12px -12px rgba(15, 23, 42, 0.35)",
                      }}
                    >
                      <Tooltip
                        title={row.statusReasons.line_match_status || "No reason provided"}
                        arrow
                        enterTouchDelay={0}
                        slotProps={{ tooltip: { sx: { fontSize: "14px", lineHeight: 1.6, maxWidth: 360 } } }}
                      >
                        <span tabIndex={0}>
                          <ModalStatusChip
                            status={overallMatchStatus.status}
                            type={overallMatchStatus.type}
                            fontWeight={400}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  );
                })}

                {modalData.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={isTwoWay ? 16 : 17}
                      align="center"
                      style={{ padding: "32px 16px" }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        Comparison details are not available for this record.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
          </TableContainer>
            <Box
              sx={{
                flexShrink: 0,
                borderTop: fullPageView ? 0 : "1px solid #e2e8f0",
                backgroundColor: fullPageView ? "transparent" : "#ffffff",
                px: fullPageView ? 0 : { xs: 1.5, sm: 2.5 },
                py: fullPageView ? 0 : 2.5,
              }}
            >
              <Box
                className="matching-view-footer"
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    xl: "minmax(0, 0.7fr) minmax(0, 1.3fr)",
                  },
                  gap: 2,
                  alignItems: "stretch",
                  "& > *": { minWidth: 0 },
                }}
              >
                <Box
                  className="matching-view-card matching-view-shipping"
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 3,
                    backgroundColor: "#ffffff",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.03)",
                    alignSelf: "stretch",
                    minHeight: fullPageView ? 100 : "auto",
                  }}
                >
                  <ShippingIcon
                    sx={{ color: "#2563eb", fontSize: 22, p: 1, borderRadius: 2, backgroundColor: "#eff6ff", boxSizing: "content-box", flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: "#0f172a",
                        mb: 1,
                        fontFamily: GILROY_BOLD,
                        borderBottom: "none",
                        pb: 0,
                        fontSize: "15px",
                      }}
                    >
                      Shipping Address
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        fontFamily: GILROY_MEDIUM,
                        color: "#475569",
                        lineHeight: 1.8,
                        overflowWrap: "break-word",
                      }}
                    >
                      {addressLines[0]}
                    </Typography>
                    {addressLines.slice(1).map((line, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          fontWeight: 400,
                          fontFamily: GILROY_MEDIUM,
                          lineHeight: 1.8,
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>
                </Box>
                <Box
                  className="matching-view-card matching-view-amounts"
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 3,
                    backgroundColor: "#ffffff",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.03)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "15px",
                      fontFamily: GILROY_BOLD,
                      fontWeight: 700,
                      lineHeight: 1.2,
                      color: "#0f172a",
                      borderBottom: "none",
                      pb: 0,
                    }}
                  >
                    Amount Summary
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        xl: "repeat(4, minmax(0, 1fr))",
                      },
                      gap: 1.25,
                    }}
                  >
                    {renderTotalTile(
                      "PO Subtotal",
                      getPoTotal(totalKeys.taxExcluded)
                    )}
                    {renderTotalTile(
                      "Invoice Subtotal",
                      getInvoiceTotal(totalKeys.taxExcluded)
                    )}
                    {renderTotalTile(
                      "PO Total (Incl. Tax)",
                      getPoTotal(totalKeys.taxIncluded)
                    )}
                    {renderTotalTile(
                      "Invoice Total (Incl. Tax)",
                      getInvoiceTotal(totalKeys.taxIncluded)
                    )}
                  </Box>
                </Box>
                {hasPaymentAdvice && (
                  <Box
                    className="matching-view-card matching-view-payment"
                    sx={{
                      gridColumn: "1 / -1",
                      px: fullPageView ? 1.5 : 1,
                      py: fullPageView ? 1.4 : 1.25,
                      borderRadius: fullPageView ? 0 : 2,
                      background: fullPageView
                        ? "linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%)"
                        : "linear-gradient(135deg, #eff6ff 0%, #f8fafc 58%, #ecfdf5 100%)",
                      border: fullPageView ? "1px solid #cbd5e1" : "1px solid #bfdbfe",
                      boxShadow: fullPageView ? "none" : "0 10px 24px rgba(15, 23, 42, 0.08)",
                      display: "flex",
                      flexDirection: "column",
                      gap: fullPageView ? 1 : 0.75,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: fullPageView ? "16px" : "14px",
                        fontFamily: GILROY_BOLD,
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: fullPageView ? "#0f172a" : "#0f4c81",
                        borderBottom: "1px solid #cbd5e1",
                        pb: 0.9,
                      }}
                    >
                      Payment Details
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          xl: "repeat(5, minmax(0, 1fr))",
                        },
                        gap: fullPageView ? 0.5 : 0.75,
                      }}
                    >
                      {renderPaymentDetail(
                        "Payment Advice No",
                        paymentAdvice?.payment_advice_number
                      )}
                    
                      {renderPaymentDetail(
                        "Transaction Date",
                        paymentAdvice?.transaction_date
                      )}
                      {renderPaymentDetail(
                        "Payment Mode",
                        paymentAdvice?.payment_product
                      )}
                      {renderPaymentDetail(
                        "UTR / RRN",
                        paymentAdvice?.utr_rrn_no
                      )}
                      {renderPaymentDetail(
                        "Advice Paid",
                        paymentReconciliation?.current_upload_total_amount ??
                          paymentReconciliation?.payment_advice?.paid_amount ??
                          paymentAdvice?.amount,
                      )}
                      {renderPaymentDetail(
                        "Balance Due",
                        reconciliationInvoice?.expected_balance ??
                          invoice?.balance_amount_payable ??
                          invoice?.balance_due,
                      )}
                      {renderPaymentDetail(
                        "Invoice Amount",
                        reconciliationInvoice?.invoice_total ??
                          invoice?.invoice_amount ??
                          invoice?.total_amount,
                      )}
                    
                    </Box>
                    {mismatchReasons.length > 0 && (
                      <Box sx={{ p: 1.25, borderRadius: 1.5, background: "linear-gradient(135deg, #fff1f2 0%, #fef2f2 100%)", border: "1px solid #fca5a5", boxShadow: "inset 4px 0 0 #dc2626" }}>
                        <Typography sx={{ fontFamily: GILROY_BOLD, color: "#991b1b", mb: 0.5 }}>Remarks</Typography>
                        {mismatchReasons.map((reason, index) => (
                          <Typography key={`${reason}-${index}`} sx={{ fontFamily: GILROY_MEDIUM, color: "#b91c1c", fontSize: "13px", mt: 0.55, lineHeight: 1.45 }}>
                            {index + 1}. {reason}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {!fullPageView && (
          <div
            style={{
              padding: "12px 24px",
              display: "flex",
              justifyContent: "flex-end",
              backgroundColor: "#ffffff",
              borderTop: "1px solid #e2e8f0",
            }}
          >
            <Button
              variant="contained"
              disableElevation
              onClick={closeDialog}
              style={{
                minWidth: 100,
                padding: "8px 20px",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                fontFamily: GILROY_MEDIUM,
              }}
            >
              Close
            </Button>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ThreeWayMatchTable;
